import React, { useEffect, useState, useContext } from "react";
import "./index.css";
import { IoIosArrowBack, IoIosArrowForward } from "react-icons/io";
import FormRendererContext from "../../../services/context/FormRendererContext";
import { Stack, Box, IconButton } from "@mui/material";
import {
  NavigateBeforeOutlined,
  NavigateNextOutlined,
  Person,
  ArrowBack,
} from "@mui/icons-material";

import axios from "axios";
import Loader from "../loader";
import { convertTimeWithTimeZone } from "../../../utils/timezone";

const AppointmentCard = ({ doctorData, providerId, activateNextClick }) => {
  const {
    scheduler_id,
    reason_id,
    setAppointment_id,
    department_id,
    setAppointmentSlotData,
    deviceType,
    patientTZ,
    journeyConfigsGlobal,
  } = useContext(FormRendererContext);

  // States
  const [isLoading, setIsLoading] = useState(true);
  const [providerTimeZone, setProviderTimeZone] = useState("America/New_York");
  const [providerTimeZoneOffset, setProviderTimeZoneOffset] = useState(-5);
  const [selectedDate, setSelectedDate] = useState(null);
  const [showSlots, setShowSlots] = useState(false);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [availableDates, setAvailableDates] = useState(new Set());
  const [dateSlotMap, setDateSlotMap] = useState(new Map());

  const months = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];

  const schedulerConfig = journeyConfigsGlobal?.scheduler;
  const hideDoctorDetails = schedulerConfig?.hideDoctorDetails || false;

  const formatDate = (date) => {
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    const year = date.getFullYear();
    return `${month}/${day}/${year}`;
  };

  const formatDateKey = (date) => {
    return date.toISOString().split("T")[0];
  };

  const fetchSlotsForMonth = async (monthDate) => {
    setIsLoading(true);

    const startOfMonth = new Date(
      monthDate.getFullYear(),
      monthDate.getMonth(),
      1
    );
    const endOfMonth = new Date(
      monthDate.getFullYear(),
      monthDate.getMonth() + 1,
      0
    );

    const startDateString = formatDate(startOfMonth);
    const endDateString = formatDate(endOfMonth);

    const API_URL = `${
      process.env?.REACT_APP_SCHEDULER_URL ||
      "https://apps-sandbox.healthblox.care/scheduler"
    }/api/v1/providers/${scheduler_id}/slots?departmentId=${department_id}&reasonId=${reason_id}&providerId=${providerId}&startDate=${startDateString}&endDate=${endDateString}`;

    try {
      const response = await axios.get(API_URL);
      const { HBx } = response?.data;
      const { appointments } = HBx || {};

      if (!appointments) {
        setIsLoading(false);
        return;
      }

      const newAvailableDates = new Set();
      const newDateSlotMap = new Map();

      appointments.forEach((appointment) => {
        const date = new Date(appointment.date);
        const dateKey = formatDateKey(date);

        appointment.patientStartTime = convertTimeWithTimeZone(
          appointment.startTime,
          date,
          providerTimeZone,
          patientTZ
        );

        newAvailableDates.add(dateKey);

        if (!newDateSlotMap.has(dateKey)) {
          newDateSlotMap.set(dateKey, []);
        }
        newDateSlotMap.get(dateKey).push(appointment);
      });

      setAvailableDates(newAvailableDates);
      setDateSlotMap(newDateSlotMap);
    } catch (error) {
      console.error("Error fetching slots:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const setProviderTimeZoneFromDepartment = async () => {
    const API_URL = `${
      process.env?.REACT_APP_SCHEDULER_URL ||
      "https://apps-sandbox.healthblox.care/scheduler"
    }/api/v1/providers/${scheduler_id}/departments`;

    try {
      const response = await axios.get(API_URL);
      const { HBx } = response?.data;
      const { departments } = HBx;
      const department = departments?.find(
        (department) => department?.departmentId === department_id
      );
      const timezoneName = department?.timezoneName || "America/New_York";
      const timezoneOffset = department?.timezoneOffset || -5;
      setProviderTimeZone(timezoneName);
      setProviderTimeZoneOffset(timezoneOffset);
    } catch (error) {
      console.error(error);
    }
  };

  const handleSlotClick = (slot) => {
    setAppointment_id(slot.appointmentId);
    setAppointmentSlotData({ slot, providerTimeZoneOffset });
    setTimeout(
      () =>
        activateNextClick(slot.appointmentId, { slot, providerTimeZoneOffset }),
      500
    );
  };

  const handleDateClick = (date) => {
    const dateKey = formatDateKey(date);
    if (availableDates.has(dateKey)) {
      setSelectedDate(date);
      setShowSlots(true);
    }
  };

  const handleMonthNavigation = (direction) => {
    setSelectedDate(null);
    const newMonth = new Date(currentMonth);
    if (direction === "next") {
      newMonth.setMonth(newMonth.getMonth() + 1);
    } else {
      newMonth.setMonth(newMonth.getMonth() - 1);
    }
    setCurrentMonth(newMonth);
    fetchSlotsForMonth(newMonth);
  };

  const convertTo12HourFormat = (time24) => {
    if (!time24) return "";
    const [hours, minutes] = time24.split(":");
    const hour24 = parseInt(hours, 10);
    const hour12 = hour24 === 0 ? 12 : hour24 > 12 ? hour24 - 12 : hour24;
    const period = hour24 >= 12 ? "PM" : "AM";
    return `${hour12}:${minutes} ${period}`;
  };

  const getUserFriendlyTimezone = (timezoneName) => {
    const friendlyNames = {
      "America/New_York": "Eastern Time (ET)",
      "America/Chicago": "Central Time (CT)",
      "America/Denver": "Mountain Time (MT)",
      "America/Los_Angeles": "Pacific Time (PT)",
      "America/Anchorage": "Alaska Time (AKT)",
      "Pacific/Honolulu": "Hawaii Time (HST)",
    };
    return friendlyNames[timezoneName] || timezoneName;
  };

  const isDatePast = (date) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const checkDate = new Date(date);
    checkDate.setHours(0, 0, 0, 0);
    return checkDate <= today;
  };

  const canNavigateToPrevMonth = () => {
    const today = new Date();
    const prevMonth = new Date(currentMonth);
    prevMonth.setMonth(prevMonth.getMonth() - 1);
    return (
      prevMonth.getMonth() >= today.getMonth() &&
      prevMonth.getFullYear() >= today.getFullYear()
    );
  };

  const renderCalendar = () => {
    const firstDayOfMonth = new Date(
      currentMonth.getFullYear(),
      currentMonth.getMonth(),
      1
    );

    // Get day of week (0 = Sunday, 1 = Monday, etc.)
    const dayOfWeek = firstDayOfMonth.getDay();
    // Convert to Monday-start (Monday = 0, Sunday = 6)
    const startOffset = dayOfWeek === 0 ? 6 : dayOfWeek - 1;

    // Get total days in current month
    const lastDayOfMonth = new Date(
      currentMonth.getFullYear(),
      currentMonth.getMonth() + 1,
      0
    );
    const totalDaysInMonth = lastDayOfMonth.getDate();

    const days = [];

    // Add empty slots for days before the month starts
    for (let i = 0; i < startOffset; i++) {
      days.push(null);
    }

    // Add all days of the current month
    for (let day = 1; day <= totalDaysInMonth; day++) {
      const currentDate = new Date(
        currentMonth.getFullYear(),
        currentMonth.getMonth(),
        day
      );

      const isPast = isDatePast(currentDate);
      const dateKey = formatDateKey(currentDate);
      const hasSlots = availableDates.has(dateKey);
      const isToday = new Date().toDateString() === currentDate.toDateString();
      const isSelected =
        selectedDate &&
        selectedDate.toDateString() === currentDate.toDateString();

      days.push({
        date: currentDate,
        isPast,
        hasSlots,
        isToday,
        isSelected,
        dateKey,
      });
    }

    return (
      <div className="calendly-calendar">
        {/* Calendar Header */}
        <div className="calendly-header">
          <button
            className={`calendly-nav-btn ${
              !canNavigateToPrevMonth() ? "disabled" : ""
            }`}
            onClick={() => handleMonthNavigation("prev")}
            disabled={!canNavigateToPrevMonth()}
          >
            <NavigateBeforeOutlined fontSize="small" />
          </button>
          <h3 className="calendly-month-title">
            {months[currentMonth.getMonth()]} {currentMonth.getFullYear()}
          </h3>
          <button
            className="calendly-nav-btn"
            onClick={() => handleMonthNavigation("next")}
          >
            <NavigateNextOutlined fontSize="small" />
          </button>
        </div>

        {/* Weekdays */}
        <div className="calendly-weekdays">
          {["MON", "TUE", "WED", "THU", "FRI", "SAT", "SUN"].map(
            (day, index) => (
              <div key={index} className="calendly-weekday">
                {day}
              </div>
            )
          )}
        </div>

        {/* Calendar Grid */}
        <div className="calendly-grid">
          {days.map((day, index) =>
            day === null ? (
              <div key={index} className="calendly-date empty"></div>
            ) : (
              <button
                key={index}
                className={`calendly-date
                ${day.isPast ? "past" : day.hasSlots ? "available" : ""}
                ${day.isToday ? "today" : ""} 
                ${day.isSelected ? "selected" : ""}
              `}
                onClick={() => !day.isPast && handleDateClick(day.date)}
                disabled={day.isPast}
              >
                {day.date.getDate()}
              </button>
            )
          )}
        </div>
      </div>
    );
  };

  const renderSlots = () => {
    if (!selectedDate)
      return (
        <div className="calendly-slots">
          <div className="calendly-slots-header">
            {deviceType === "mobile" && (
              <button
                className="calendly-back-btn"
                onClick={() => setShowSlots(false)}
              >
                <ArrowBack fontSize="small" />
              </button>
            )}
          </div>
          <div className="calendly-time-slots">
            <div className="calendly-no-slots">Please choose a date</div>
          </div>
        </div>
      );

    const dateKey = formatDateKey(selectedDate);
    const slots = dateSlotMap.get(dateKey) || [];

    return (
      <div className="calendly-slots">
        <div className="calendly-slots-header">
          {deviceType === "mobile" && (
            <button
              className="calendly-back-btn"
              onClick={() => setShowSlots(false)}
            >
              <ArrowBack fontSize="small" />
            </button>
          )}
          <div className="calendly-selected-date">
            <h4>
              {selectedDate.toLocaleDateString("en-US", {
                weekday: "long",
                month: "long",
                day: "numeric",
              })}
            </h4>
            <span className="calendly-timezone">
              {getUserFriendlyTimezone(providerTimeZone)}
            </span>
          </div>
        </div>

        <div className="calendly-time-slots">
          {slots.length > 0 ? (
            slots.map((slot) => (
              <button
                key={slot.appointmentId}
                className="calendly-time-slot"
                onClick={() => handleSlotClick(slot)}
              >
                {convertTo12HourFormat(slot.patientStartTime)}
              </button>
            ))
          ) : (
            <div className="calendly-no-slots">No available slots</div>
          )}
        </div>
      </div>
    );
  };

  // Initialize component
  useEffect(() => {
    const initializeComponent = async () => {
      await setProviderTimeZoneFromDepartment();
      await fetchSlotsForMonth(currentMonth);
    };
    initializeComponent();
  }, []);

  if (isLoading) {
    return (
      <div className="calendly-loading">
        <Loader />
      </div>
    );
  }

  // Mobile Layout
  if (deviceType === "mobile") {
    return (
      <div className="calendly-mobile">
        {/* Doctor Section */}
        {!hideDoctorDetails && (
          <div className="calendly-doctor-mobile">
            <div className="calendly-doctor-avatar">
              {doctorData?.display_image ? (
                <img src={doctorData?.display_image} alt="Doctor" />
              ) : (
                <Person />
              )}
            </div>
            <div className="calendly-doctor-info">
              <h3>{doctorData?.displayName}</h3>
              {doctorData?.specialty && <p>{doctorData?.specialty}</p>}
            </div>
          </div>
        )}

        {/* Content */}
        <div className="calendly-content-mobile">
          {showSlots ? renderSlots() : renderCalendar()}
        </div>
      </div>
    );
  }

  // Desktop Layout
  return (
    <div
      className={`${
        !hideDoctorDetails
          ? "calendly-desktop"
          : "calendly-desktop max-w-[900px] m-auto"
      }`}
    >
      <div className="calendly-container">
        {/* Doctor Info */}
        {!hideDoctorDetails && (
          <div className="calendly-doctor-desktop">
            <div className="calendly-doctor-avatar">
              {doctorData?.display_image ? (
                <img src={doctorData?.display_image} alt="Doctor" />
              ) : (
                <Person />
              )}
            </div>
            <h3>{doctorData?.displayName}</h3>
            {doctorData?.specialty && <p>{doctorData?.specialty}</p>}
          </div>
        )}

        {/* Calendar & Slots */}
        <div className="calendly-booking-area">
          <div className="calendly-calendar-section">{renderCalendar()}</div>

          {showSlots && (
            <div className="calendly-slots-section">{renderSlots()}</div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AppointmentCard;
